'use client';

import { Plus, X, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import { SECTION_METADATA, type SectionType } from '@/lib/templates/schema';
import { useState, useEffect } from 'react';

export function AddSectionMenu() {
  const { addSection } = useReceiptBuilder();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // For SSR safety - only render portal after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleAddSection = (type: SectionType) => {
    addSection(type);
    setIsOpen(false);
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      <Button variant="outline" className="w-full gap-2" onClick={() => setIsOpen(true)}>
        <Plus className="h-4 w-4" />
        Add Section
      </Button>

      {/* Full-screen mobile modal - rendered via portal to escape stacking context */}
      {mounted && isOpen && createPortal(
        <div className="fixed inset-0 flex flex-col bg-background" style={{ zIndex: 99999 }}>
          {/* Header */}
          <div className="px-4 py-4 border-b shrink-0 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Add Section</h2>
              <p className="text-sm text-muted-foreground">
                Select a section type to add
              </p>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-muted rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Section count */}
          <div className="px-4 py-2 border-b bg-muted/30">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {SECTION_METADATA.length} section types available
            </p>
          </div>
          
          {/* Scrollable List */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
            <div className="space-y-2">
              {SECTION_METADATA.map((meta) => (
                <button
                  key={meta.type}
                  onClick={() => handleAddSection(meta.type)}
                  className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-muted active:bg-muted/80 transition-colors text-left border border-border bg-background shadow-sm"
                >
                  <span className="text-2xl">{meta.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{meta.label}</div>
                    <div className="text-sm text-muted-foreground">
                      {meta.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Bottom safe area + scroll hint */}
          <div className="shrink-0 py-3 px-4 border-t bg-muted/30 safe-area-bottom">
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <ChevronDown className="h-4 w-4 animate-bounce" />
              <span>Scroll to see all options</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
